import * as Y from 'yjs';
import type { GraphEdge, GraphNode, ParamBag, ParamValue } from './types';

/**
 * The document as a CRDT.
 *
 * `Graph` keeps plain objects because everything downstream — the evaluator,
 * the canvas, the agent operations — reads them thousands of times a frame and
 * should not pay for a CRDT lookup to do it. So the plain model stays, and this
 * becomes the thing that is actually *mutated*: every write goes through here,
 * and the plain objects are a materialised view kept in step by an observer.
 *
 * The shape is chosen for how texture graphs actually conflict, which is
 * hardly ever. Two people rarely touch one node, and when they do it is usually
 * different parameters of it. So:
 *
 *  - nodes and edges are keyed maps, which makes concurrent adds and removes
 *    commute without any merge logic at all;
 *  - a node's fields are a map, so moving a node and renaming it are
 *    independent;
 *  - **parameters are their own map**, so two people adjusting different
 *    parameters of the same node both keep their change. Storing the parameter
 *    bag as one value would make that a coin toss.
 *
 * Last-writer-wins per key, which is the same choice Figma made and for the
 * same reason: the conflicts that survive this are ones where two people really
 * did set one value, and there is no answer there but to pick one.
 *
 * Nothing here knows about the network. This is the data model; carrying
 * updates between clients is a separate concern that plugs into the same doc.
 */

/**
 * Marks a transaction as this user's own work.
 *
 * The undo manager tracks only this origin, which is the whole reason undo can
 * become per-user: a remote edit arrives under a different origin and is
 * therefore invisible to the local undo stack. Nobody can undo your work but
 * you, and you cannot undo theirs.
 */
export const LOCAL_ORIGIN = 'local';

/** Applied when loading a document, so opening a file is not an undo step. */
export const LOAD_ORIGIN = 'load';

export interface YGraph {
  doc: Y.Doc;
  /** Document-level settings: name, resolution, filtering. */
  meta: Y.Map<unknown>;
  nodes: Y.Map<Y.Map<unknown>>;
  edges: Y.Map<Y.Map<unknown>>;
}

export function createYGraph(): YGraph {
  const doc = new Y.Doc();
  return yGraphForDoc(doc);
}

export function yGraphForDoc(doc: Y.Doc): YGraph {
  return {
    doc,
    meta: doc.getMap('meta'),
    nodes: doc.getMap('nodes'),
    edges: doc.getMap('edges'),
  };
}

/** Materialize a standalone Yjs graph from an untrusted full or incremental update. */
export function createYGraphFromUpdate(update: Uint8Array): YGraph {
  const graph = createYGraph();
  Y.applyUpdate(graph.doc, update, LOAD_ORIGIN);
  return graph;
}

/** Clone state, including tombstones/delete sets, into an isolated document. */
export function cloneYGraph(source: YGraph): YGraph {
  return createYGraphFromUpdate(encodeYGraphState(source));
}

/** Complete encoded state. Unlike a state vector, this includes the delete set. */
export function encodeYGraphState(graph: YGraph): Uint8Array {
  return Y.encodeStateAsUpdate(graph.doc);
}

/** Changes needed by a peer that advertised `stateVector`. */
export function encodeYGraphDelta(graph: YGraph, stateVector: Uint8Array): Uint8Array {
  return Y.encodeStateAsUpdate(graph.doc, stateVector);
}

export function encodeYGraphStateVector(graph: YGraph): Uint8Array {
  return Y.encodeStateVector(graph.doc);
}

/**
 * SHA-256 fingerprint of complete encoded CRDT state, including deletions.
 *
 * Encoding the same logical update history is deterministic. A reconstructed equivalent that encodes
 * differently may cause a safe false conflict, but can never accept a stale delete-only checkout.
 */
export async function completeYGraphDigest(graph: YGraph): Promise<string> {
  const bytes = new Uint8Array(encodeYGraphState(graph));
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

// ------------------------------------------------------------------ writing

export function paramsToY(params: ParamBag): Y.Map<unknown> {
  const map = new Y.Map<unknown>();
  // Values are cloned on the way in. A parameter bag handed to the graph can be
  // reused by the caller, and a live reference inside the document would let it
  // change underneath us without any transaction at all.
  for (const [key, value] of Object.entries(params)) map.set(key, structuredClone(value));
  return map;
}

export function nodeToY(node: GraphNode): Y.Map<unknown> {
  const map = new Y.Map<unknown>();
  map.set('type', node.type);
  map.set('x', node.x);
  map.set('y', node.y);
  if (node.title !== undefined) map.set('title', node.title);
  if (node.collapsed) map.set('collapsed', true);
  map.set('params', paramsToY(node.params));
  return map;
}

export function edgeToY(edge: GraphEdge): Y.Map<unknown> {
  const map = new Y.Map<unknown>();
  map.set('from', edge.from);
  map.set('fromPort', edge.fromPort);
  map.set('to', edge.to);
  map.set('toPort', edge.toPort);
  return map;
}

// ------------------------------------------------------------------ reading

export function yToParams(map: Y.Map<unknown> | undefined): ParamBag {
  const params: ParamBag = {};
  if (!map) return params;
  for (const [key, value] of map.entries()) params[key] = value as ParamValue;
  return params;
}

export function yToNode(id: string, map: Y.Map<unknown>): GraphNode {
  const node: GraphNode = {
    id,
    type: String(map.get('type')),
    x: Number(map.get('x')) || 0,
    y: Number(map.get('y')) || 0,
    params: yToParams(map.get('params') as Y.Map<unknown> | undefined),
  };
  const title = map.get('title');
  if (typeof title === 'string' && title) node.title = title;
  if (map.get('collapsed') === true) node.collapsed = true;
  return node;
}

export function yToEdge(id: string, map: Y.Map<unknown>): GraphEdge {
  return {
    id,
    from: String(map.get('from')),
    fromPort: String(map.get('fromPort') ?? 'out'),
    to: String(map.get('to')),
    toPort: String(map.get('toPort')),
  };
}
